import { isPerpInstrument } from '@/features/hedge/lib/instrument-display'
import type { Instrument } from '@/types/generated/common/types'

/**
 * `Ticker.next_funding_time` / `IncrementalTicker.next_funding_time` → Date. Both are Unix
 * NANOSECONDS (even though IncrementalTicker.timestamp is ms), generated as a decimal string
 * (`forceLong=string`), so the ns → ms division runs in BigInt before narrowing to a Number.
 * Null when absent, unparseable or non-positive.
 */
export function nextFundingDate(ns: string | null | undefined): Date | null {
  if (!ns) return null
  let value: bigint
  try {
    value = BigInt(ns)
  } catch {
    return null
  }
  if (value <= 0n) return null
  return new Date(Number(value / 1_000_000n))
}

/** Time left to a funding settlement as "HH:MM:SS" (hours unbounded), clamped at "00:00:00". */
export function formatFundingCountdown(remainingMs: number): string {
  const totalSeconds = Number.isFinite(remainingMs)
    ? Math.max(0, Math.floor(remainingMs / 1000))
    : 0
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return [hours, minutes, seconds].map((n) => String(n).padStart(2, '0')).join(':')
}

export type FundingSchedule =
  | { kind: 'scheduled'; at: Date }
  /** A perp with no discrete settlement (no funding interval on the instrument, e.g.
   * Deribit/Derive's continuous funding) — the backend sends no next funding time for these. */
  | { kind: 'continuous' }
  /** Nothing to show: not a perp, the instrument isn't resolved yet, or an interval-funded
   * perp whose next time hasn't arrived. */
  | { kind: 'none' }

/**
 * What a funding display should show next to the rate. A next funding time always wins. Without
 * one, "continuous" is only claimed for a resolved perp that has no funding interval — never by
 * venue name — so an interval-funded perp whose time simply hasn't arrived shows nothing.
 */
export function resolveFundingSchedule(
  nextFundingTimeNs: string | null | undefined,
  instrument: Pick<Instrument, 'symbol' | 'fundingInterval'> | null | undefined
): FundingSchedule {
  const at = nextFundingDate(nextFundingTimeNs)
  if (at) return { kind: 'scheduled', at }
  if (!instrument || !isPerpInstrument(instrument)) return { kind: 'none' }
  const interval = parseFloat(instrument.fundingInterval ?? '')
  return interval > 0 ? { kind: 'none' } : { kind: 'continuous' }
}
